// src/hooks/useSelections.js
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import {
  uploadFile,
  buildUploadContext,
} from "../services/cloudinaryService";

/**
 * Get ALL selections data for a project (consolidated approach)
 * This is the main hook for the Client Selections page
 */
export const useClientSelectionsData = (projectId) => {
  return useQuery({
    queryKey: ["allSelectionsData", projectId],
    queryFn: async () => {
      const getClientSelections = httpsCallable(
        functions,
        "getClientSelections",
      );
      const response = await getClientSelections({ projectId });

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to fetch selections");
      }

      // Debug logging (remove after confirming fix)
      console.log("🔥 Firebase Response:", response.data);

      // The response.data already contains all the selections data
      // Return it directly (it already has clientSelections, paintSelections, etc.)
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Keep your existing individual hooks for backward compatibility
// or for pages that only need specific data

/**
 * Get client selections for a project (legacy/individual)
 */
export const useClientSelections = (projectId) => {
  return useQuery({
    queryKey: ["clientSelections", projectId],
    queryFn: async () => {
      const getSelections = httpsCallable(functions, "getClientSelections");
      const response = await getSelections({ projectId });

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to fetch selections");
      }

      // Return just the client selections part
      return response.data.clientSelections;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Get paint selections for a project
 */
export const usePaintSelections = (projectId) => {
  return useQuery({
    queryKey: ["paintSelections", projectId],
    queryFn: async () => {
      // Can either call the consolidated function and extract paint data
      // or keep calling a separate getPaintSelections function if it exists
      const getSelections = httpsCallable(functions, "getClientSelections");
      const response = await getSelections({ projectId });

      if (!response.data.success) {
        throw new Error(
          response.data.error || "Failed to fetch paint selections",
        );
      }

      return response.data.paintSelections || [];
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Get tile options for a project
 */
export const useTileOptions = (projectId) => {
  return useQuery({
    queryKey: ["tileOptions", projectId],
    queryFn: async () => {
      const getSelections = httpsCallable(functions, "getClientSelections");
      const response = await getSelections({ projectId });

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to fetch tile options");
      }

      return response.data.tileOptions || [];
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Get owner purchases for a project
 */
export const useOwnerPurchases = (projectId) => {
  return useQuery({
    queryKey: ["ownerPurchases", projectId],
    queryFn: async () => {
      const getSelections = httpsCallable(functions, "getClientSelections");
      const response = await getSelections({ projectId });

      if (!response.data.success) {
        throw new Error(
          response.data.error || "Failed to fetch owner purchases",
        );
      }

      return response.data.ownerPurchases || [];
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Mark selection as removed (soft delete)
 */
export const useMarkSelectionRemoved = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ type, id }) => {
      const markRemoved = httpsCallable(functions, "markSelectionRemoved");
      const response = await markRemoved({ type, id });

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to remove selection");
      }

      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate queries to refresh the data (removed items will be filtered out)
      queryClient.invalidateQueries(["allSelectionsData", variables.projectId]);
      queryClient.invalidateQueries([
        "clientSelectionsData",
        variables.projectId,
      ]);
    },
  });
};

/**
 * Delete a selection (paint or tile)
 */
export const useDeleteSelection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ type, id, projectId }) => {
      const deleteSelection = httpsCallable(functions, "deleteSelection");
      const response = await deleteSelection({ type, id });

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to delete selection");
      }

      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate all related queries
      queryClient.invalidateQueries(["allSelectionsData", variables.projectId]);

      // Also invalidate specific queries
      if (variables.type === "paint") {
        queryClient.invalidateQueries(["paintSelections", variables.projectId]);
      } else if (variables.type === "tile") {
        queryClient.invalidateQueries(["tileOptions", variables.projectId]);
      }
    },
  });
};

/**
 * Add paint selection mutation
 */
export const useAddPaintSelection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paintData) => {
      const addPaintSelection = httpsCallable(functions, "addPaintSelection");
      const response = await addPaintSelection(paintData);

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to add paint selection");
      }

      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries(["allSelectionsData", variables.projectId]);
      queryClient.invalidateQueries(["paintSelections", variables.projectId]);
    },
  });
};

/**
 * Update paint selection mutation
 */
export const useUpdatePaintSelection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ paintId, updates, projectId, displayLabel }) => {
      const updatePaintSelection = httpsCallable(
        functions,
        "updatePaintSelection",
      );
      const response = await updatePaintSelection({
        paintId,
        updates,
        projectId,
        displayLabel,
      });

      if (!response.data.success) {
        throw new Error(
          response.data.error || "Failed to update paint selection",
        );
      }

      return response.data.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["allSelectionsData", variables.projectId]);
      queryClient.invalidateQueries(["paintSelections", variables.projectId]);
    },
  });
};

/**
 * Remove paint selection mutation (legacy naming)
 */
export const useRemovePaintSelection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ selectionId, projectId }) => {
      const deleteSelection = httpsCallable(functions, "deleteSelection");
      const response = await deleteSelection({
        type: "paint",
        id: selectionId,
      });

      if (!response.data.success) {
        throw new Error(
          response.data.error || "Failed to remove paint selection",
        );
      }

      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["allSelectionsData", variables.projectId]);
      queryClient.invalidateQueries(["paintSelections", variables.projectId]);
    },
  });
};

/**
 * Add tile option mutation
 */
export const useAddTileOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tileData) => {
      const addTileOption = httpsCallable(functions, "addTileOption");
      const response = await addTileOption(tileData);

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to add tile option");
      }

      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["allSelectionsData", variables.projectId]);
      queryClient.invalidateQueries(["tileOptions", variables.projectId]);
    },
  });
};

/**
 * Remove tile option mutation (legacy naming)
 */
export const useRemoveTileOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ optionId, projectId }) => {
      const deleteSelection = httpsCallable(functions, "deleteSelection");
      const response = await deleteSelection({ type: "tile", id: optionId });

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to remove tile option");
      }

      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["allSelectionsData", variables.projectId]);
      queryClient.invalidateQueries(["tileOptions", variables.projectId]);
    },
  });
};

/**
 * Add owner purchase mutation
 */
export const useAddOwnerPurchase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (purchaseData) => {
      const addOwnerPurchase = httpsCallable(functions, "addOwnerPurchase");
      const response = await addOwnerPurchase(purchaseData);

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to add owner purchase");
      }

      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["allSelectionsData", variables.projectId]);
      queryClient.invalidateQueries(["ownerPurchases", variables.projectId]);
    },
  });
};

/**
 * Update owner purchase mutation
 */
export const useUpdateOwnerPurchase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ purchaseId, updates, projectId }) => {
      const updateOwnerPurchase = httpsCallable(
        functions,
        "updateOwnerPurchase",
      );
      const response = await updateOwnerPurchase({
        purchaseId,
        updates,
        projectId,
      });

      if (!response.data.success) {
        throw new Error(
          response.data.error || "Failed to update owner purchase",
        );
      }

      return response.data.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["allSelectionsData", variables.projectId]);
      queryClient.invalidateQueries(["ownerPurchases", variables.projectId]);
    },
  });
};

/**
 * Fetch Sherwin-Williams paint colors for the color picker
 * Caches the result since paint colors don't change often
 *
 * @returns {Object} Query result with data, isLoading, error
 */
export const useSWPaintColors = () => {
  return useQuery({
    queryKey: ["swPaintColors"],
    queryFn: async () => {
      const getSWPaintColors = httpsCallable(functions, "getSWPaintColors");
      const response = await getSWPaintColors();

      if (!response.data.success) {
        throw new Error(
          response.data.error || "Failed to fetch SW paint colors",
        );
      }

      return response.data.data;
    },
    staleTime: 1000 * 60 * 60 * 24, // Cache for 24 hours (colors don't change)
    cacheTime: 1000 * 60 * 60 * 24 * 7, // Keep in cache for 7 days
  });
};

/**
 * Upload selection documents mutation.
 *
 * Uploads to Cloudinary via the shared `cloudinaryService` (so the file
 * is tagged with standardized context metadata), then stores the
 * resulting URL in Airtable via the `uploadSelectionDocument` callable.
 *
 * @returns {import('@tanstack/react-query').UseMutationResult}
 *
 * Mutation input:
 * @param {Object} vars
 * @param {string} vars.projectId - Airtable project record ID
 * @param {string} [vars.projectName] - Human-readable project name (for context)
 * @param {string} vars.category - Airtable-side category key (e.g. "roofGutter")
 * @param {string} vars.categorySlug - Normalized snake_case slug for Cloudinary (e.g. "roof_gutter")
 * @param {string} vars.categoryLabel - Human-readable category label
 * @param {string} [vars.uploadedByName] - Canonical Airtable Team name for Cloudinary `uploaded_by_name` context
 * @param {File} vars.file - File to upload
 */
export const useUploadSelectionDocuments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      projectName,
      category,
      categorySlug,
      categoryLabel,
      uploadedByName,
      file,
    }) => {
      // Derive a snake_case slug from camelCase `category` when callers
      // haven't been migrated to pass `categorySlug` explicitly.
      const resolvedSlug =
        categorySlug ||
        (category || "")
          .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
          .replace(/[A-Z]+/g, (m) => m.toLowerCase())
          .toLowerCase();
      const resolvedLabel = categoryLabel || category || "";

      // Step 1: Upload to Cloudinary via the shared service with context
      const uploadContext = buildUploadContext({
        projectId,
        projectName: projectName || "",
        category: resolvedSlug,
        categoryLabel: resolvedLabel,
        sourceTable: "custom_selections",
        uploadedByName,
        file,
      });

      const cloudinaryResult = await uploadFile(file, {
        projectId,
        sourceTable: "custom_selections",
        category: resolvedSlug,
        uploadContext,
      });

      // Step 2: Store URL in Airtable via Firebase
      const uploadSelectionDocument = httpsCallable(
        functions,
        "uploadSelectionDocument",
      );
      const response = await uploadSelectionDocument({
        projectId,
        category,
        fileUrl: cloudinaryResult.url,
        fileName: file.name,
      });

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to upload");
      }

      return response.data.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["allSelectionsData", variables.projectId]);
      queryClient.invalidateQueries(["clientSelections", variables.projectId]);
    },
  });
};

/**
 * Delete selection document mutation
 * Removes an attachment from the Custom Selections table for a given category.
 */
export const useDeleteSelectionDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, category, attachmentId }) => {
      const deleteSelectionDocument = httpsCallable(
        functions,
        "deleteSelectionDocument",
      );
      const response = await deleteSelectionDocument({
        projectId,
        category,
        attachmentId,
      });

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to delete document");
      }

      return response.data.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["allSelectionsData", variables.projectId]);
      queryClient.invalidateQueries(["clientSelections", variables.projectId]);
    },
  });
};

/**
 * Fetches dropdown options for Single Select and Multiple Select
 * fields in the Custom Selections table from the Airtable Meta API.
 *
 * Returns data shaped like:
 * {
 *   roofMaterial: ["Shingle", "Metal"],
 *   roofColor: ["Polar White", "Light Stone", ...],
 *   sidingTypes: ["Fiber Cement Board", "Brick", "Stone", "Wood"],
 *   interiorDoorPattern: ["Avalon Textured", "Cambridge Smooth", ...],
 *   ...
 * }
 *
 * Cached for 30 minutes since options rarely change.
 *
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export const useSelectionFieldOptions = () => {
  return useQuery({
    queryKey: ["selectionFieldOptions"],
    queryFn: async () => {
      const getSelectionFieldOptions = httpsCallable(
        functions,
        "getSelectionFieldOptions",
      );
      const result = await getSelectionFieldOptions();
      return result.data?.data || result.data || {};
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
  });
};

/**
 * Mutation hook for updating client selections inline.
 *
 * Calls the updateClientSelections Firebase Function with
 * a record ID and an updates object. On success, invalidates
 * the "clientSelections" query key to refetch fresh data.
 *
 * @returns {import('@tanstack/react-query').UseMutationResult}
 *
 * @example
 * const updateSelections = useUpdateClientSelections();
 *
 * const handleSave = async (sectionUpdates) => {
 *   await updateSelections.mutateAsync({
 *     recordId: clientSelections.id,
 *     updates: sectionUpdates,
 *   });
 * };
 */
export const useUpdateClientSelections = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ recordId, updates, projectId }) => {
      const updateClientSelections = httpsCallable(
        functions,
        "updateClientSelections",
      );
      const result = await updateClientSelections({
        recordId,
        updates,
        projectId,
      });
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allSelectionsData"] });
    },
    onError: (error) => {
      console.error("Failed to update client selections:", error);
    },
  });
};
